/**
 * Hook that watches for AI credit threshold events and shows toast notifications.
 * Subscribes to recent threshold events and displays toasts for new crossings.
 */

import { useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export function useCreditThresholdToasts() {
  const { t } = useTranslation("common");
  const events = useQuery(api.ai_credit_queries.getRecentThresholdEvents, {});
  const shownRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!events || events.length === 0) return;

    for (const event of events) {
      // Create a unique key per threshold+timestamp to avoid duplicate toasts
      const key = `${event.threshold}-${event.timestamp}`;
      if (shownRef.current.has(key)) continue;
      shownRef.current.add(key);

      const thresholdKey = String(event.threshold) as "75" | "90" | "100";
      const validThresholds = ["75", "90", "100"];
      if (!validThresholds.includes(thresholdKey)) continue;

      const title = t(`creditThreshold.${thresholdKey}.title`);
      const description = t(`creditThreshold.${thresholdKey}.description`);

      if (event.threshold === 100) {
        toast.error(title, {
          description,
          duration: 8000,
        });
      } else if (event.threshold === 90) {
        toast.warning(title, {
          description,
          duration: 6000,
        });
      } else {
        toast.info(title, {
          description,
          duration: 5000,
        });
      }
    }
  }, [events, t]);
}
